import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../lib/auth";
import { Header } from "../components/Chrome";
import { toast } from "sonner";

const TYPES = [
  ["convenience_store", "Convenience Store"],
  ["gas_station", "Gas Station"],
  ["hospital", "Hospital Gift Shop"],
  ["cafeteria", "Cafeteria"],
  ["grab_and_go", "Grab-and-Go Outlet"],
  ["independent_retailer", "Independent Retailer"],
  ["other", "Other"],
];

export default function Apply() {
  const { register, formatError } = useAuth();
  const [f, setF] = useState({
    business_name: "", contact_name: "", email: "", phone: "",
    business_type: "convenience_store", address: "", password: "", message: "",
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await register(f);
      toast.success("Application received. We'll email you within 1 business day.");
      nav("/portal");
    } catch (ex) {
      setErr(formatError(ex.response?.data?.detail) || ex.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-paper text-ink">
      <Header />
      <div className="pt-32 pb-24 px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4 lg:sticky lg:top-32 self-start">
            <div className="overline text-black/50 mb-6">Wholesale Application</div>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl uppercase leading-[0.85] tracking-tighter">
              Open<br/>your<br/><em className="font-editorial italic tracking-normal">account.</em>
            </h1>
            <p className="mt-8 text-lg max-w-sm">Approval typically within 1 business day. Note: online ordering is currently disabled — for pricing and orders please use our <Link to="/enquiry" className="link-underline font-semibold">Request Pricing form</Link>.</p>
            <div className="mt-10 border border-ink p-5">
              <div className="overline text-black/50 mb-2">Already applied?</div>
              <Link to="/login" className="link-underline font-semibold" data-testid="apply-to-login">Log in →</Link>
            </div>
          </div>
          <motion.form
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16,1,0.3,1] }}
            onSubmit={submit}
            className="lg:col-span-7 lg:col-start-6 border border-ink p-8 md:p-10 hard-shadow bg-paper space-y-5"
            data-testid="apply-form"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="overline block mb-2">Business name</label>
                <input required value={f.business_name} onChange={e=>setF({...f, business_name:e.target.value})} className="field" data-testid="apply-business" />
              </div>
              <div>
                <label className="overline block mb-2">Contact name</label>
                <input required value={f.contact_name} onChange={e=>setF({...f, contact_name:e.target.value})} className="field" data-testid="apply-contact" />
              </div>
              <div>
                <label className="overline block mb-2">Email</label>
                <input required type="email" value={f.email} onChange={e=>setF({...f, email:e.target.value})} className="field" data-testid="apply-email" />
              </div>
              <div>
                <label className="overline block mb-2">Phone</label>
                <input required value={f.phone} onChange={e=>setF({...f, phone:e.target.value})} className="field" data-testid="apply-phone" />
              </div>
              <div className="md:col-span-2">
                <label className="overline block mb-2">Business type</label>
                <select value={f.business_type} onChange={e=>setF({...f, business_type:e.target.value})} className="field" data-testid="apply-type">
                  {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="overline block mb-2">Business address</label>
                <input value={f.address} onChange={e=>setF({...f, address:e.target.value})} className="field" data-testid="apply-address" />
              </div>
              <div className="md:col-span-2">
                <label className="overline block mb-2">Password (min 6)</label>
                <input required type="password" minLength={6} value={f.password} onChange={e=>setF({...f, password:e.target.value})} className="field" data-testid="apply-password" />
              </div>
              <div className="md:col-span-2">
                <label className="overline block mb-2">Anything else? (optional)</label>
                <textarea rows={3} value={f.message} onChange={e=>setF({...f, message:e.target.value})} className="field" data-testid="apply-message" />
              </div>
            </div>
            {err && <div className="text-sm text-soda font-semibold" data-testid="apply-error">{err}</div>}
            <button disabled={busy} className="btn-primary w-full md:w-auto" data-testid="apply-submit">
              {busy ? "Submitting…" : "Submit application →"}
            </button>
          </motion.form>
        </div>
      </div>
    </div>
  );
}
